import { useQuery } from '@tanstack/react-query';

import HabitatLink from '@/components/HabitatLink';
import LiveBadge from '@/components/LiveBadge';
import { getHabitatQueryOptions } from '@/queries/habitats';
import { HabitatListItem } from '@/queries/habitats/types';
import { trackMP } from '@/shared/mp';

import HabitatProfileImageLink from './HabitatProfileImageLink';

interface HomeLiveStreamInfoBarProps {
  habitat: HabitatListItem;
}

const HomeLiveStreamInfoBar = ({ habitat }: HomeLiveStreamInfoBarProps) => {
  const { data: description } = useQuery({
    ...getHabitatQueryOptions(habitat?.slug, habitat?.zoo.slug),
    select: (data) => data.habitat.description,
  });

  return (
    <div className="relative z-10 flex bg-primary-green px-4 py-3 text-white mobile:items-center desktop:justify-between desktop:bg-white desktop:px-6 desktop:py-5 desktop:text-black desktop:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.07)]">
      <div className="shrink-0 pb-4 mobile:hidden">
        <HabitatProfileImageLink
          habitatId={habitat._id}
          slug={habitat.slug}
          zooSlug={habitat.zoo.slug}
          profileImage={habitat.profileImage}
          title={habitat.title}
          zooLogo={habitat.zoo.logo}
          zooName={habitat.zoo.name}
          entryPoint="featured-habitat"
        />
      </div>
      <div className="min-w-0 grow pl-8 mobile:hidden">
        <div className="flex items-center gap-2">
          <HabitatLink
            productsPromptModalState={{ entryPoint: 'featured-habitat' }}
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            className="preflight preflight-a text-xl font-medium leading-none text-grey-1 hover:text-grey-3"
            onClick={() => {
              trackMP('visit-habitat-link-clicked', {
                habitatId: habitat._id,
                entryPoint: 'featured-habitat',
              });
            }}
          >
            {habitat.title}
          </HabitatLink>
          <LiveBadge />
        </div>
        {habitat.animal && (
          <p className="preflight preflight-p mt-[7px] text-[10px] font-bold uppercase leading-[11px] text-stone-500">
            {habitat.animal} AT {habitat.zoo.name}
          </p>
        )}
        <p className="preflight preflight-p mt-[11px] max-w-[628px] text-sm leading-[21px] text-grey-6">{description}</p>
      </div>
      <h4 className="preflight preflight-h4 min-w-0 grow truncate pr-2 text-sm font-bold desktop:hidden">
        Featured habitat: <span className="text-olive-4">{habitat.title}</span>
      </h4>
      <div className="flex shrink-0 items-center">
        <HabitatLink
          productsPromptModalState={{ entryPoint: 'featured-habitat' }}
          habitatId={habitat._id}
          slug={habitat.slug}
          zooSlug={habitat.zoo.slug}
          className="preflight preflight-a rounded-full border border-transparent bg-white px-3 py-1 text-xs font-medium text-black desktop:bg-primary-green desktop:px-4 desktop:py-2 desktop:text-sm desktop:font-medium desktop:text-white desktop:hover:bg-secondary-green"
          onClick={() => {
            trackMP('visit-habitat-link-clicked', {
              habitatId: habitat._id,
              entryPoint: 'featured-habitat',
            });
          }}
        >
          Visit<span className="mobile:sr-only"> habitat</span>
        </HabitatLink>
      </div>
    </div>
  );
};

export default HomeLiveStreamInfoBar;
